import { useEffect, useMemo, useState } from 'react';
import { IUiWorkCalendar, useUi } from '../provider/UiContext';
import { createWorkCalendarHolidayPredicate, workCalendarDayKey } from '../lib/workCalendar';

const emptyWorkCalendar = (): IUiWorkCalendar => ({ extraWorkingDays: [], extraHolidays: [], holidayNames: new Map() });

/**
 * Рабочий календарь для полей дат. Загрузчик — `load` или `loadWorkCalendar` из `UiProvider`; кэш запроса — забота
 * загрузчика. Пока календарь не загружен, без загрузчика или при `enabled = false` выходные — суббота и воскресенье.
 */
export const useWorkCalendar = (enabled: boolean, load?: () => Promise<IUiWorkCalendar>) => {
    const providerLoad = useUi().loadWorkCalendar;
    const loader = load ?? providerLoad;
    const [calendar, setCalendar] = useState<IUiWorkCalendar>(emptyWorkCalendar);

    useEffect(() => {
        if (!enabled || !loader) return;
        let active = true;

        loader().then(
            (workCalendar) => {
                if (active) setCalendar(workCalendar);
            },
            // Отказ загрузчика — вне контракта провайдера; поле остаётся на обычной неделе.
            () => undefined,
        );

        return () => {
            active = false;
        };
    }, [enabled, loader]);

    const isHoliday = useMemo(() => createWorkCalendarHolidayPredicate(calendar), [calendar]);

    const getHolidayName = useMemo(() => {
        const names = calendar.holidayNames;
        return (date: Date): string | undefined => names.get(workCalendarDayKey(date));
    }, [calendar]);

    return {
        isHoliday,
        getHolidayName,
        extraWorkingDays: calendar.extraWorkingDays,
        extraHolidays: calendar.extraHolidays,
        holidayNames: calendar.holidayNames,
    };
};
